import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, Select } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { SelectOption } from 'contracts/Common';
import { useAuth, useDocks, useValidation, useWarehouses } from 'hooks';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { CreateDockReservedIntervalActions as CreateActions } from 'store/ducks/dockReservedIntervals';
import { TimeHelper } from 'utils';
import { CreateIntervalValidator } from 'validators/DockReservedIntervals';
import * as S from './styles';

interface Props {
  onCreate?: () => void;
}

interface GeneralState {
  warehouseId?: string | number;
}

export const IntervalCreationForm: React.FC<Props> = ({ onCreate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const [intervalType, setIntervalType] = useState<
    'period' | 'single' | 'weekdays' | null
  >(null);
  const [generalState, setGeneralState] = useState<GeneralState>({});

  const { profile } = useAuth();
  const { handleFormErrors, handleApiErrors } = useValidation();
  const { warehouseOptions, loadingWarehouses, fetchWarehouses } =
    useWarehouses();
  const { dockOptions, loadingDocks, fetchDocks } = useDocks();

  const { loading: creatingInterval, validationErrors } = useSelector(
    (state: RootState) => state.createDockReservedInterval
  );

  const onWarehouseChange = useCallback(
    (option: SelectOption | null): void => {
      formRef.current?.setFieldValue('dockIds', []);
      setGeneralState((state) => ({
        ...state,
        warehouseId: option?.value,
      }));
      if (option) {
        fetchDocks({ warehouseId: option.value });
      }
    },
    [fetchDocks]
  );

  const onTimeBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value, name } = event.target;
      // manipulate the start time to be the next closest 5 minutes
      const newStartTime = TimeHelper.roundUpMinutes(value, 5);
      formRef.current?.setFieldValue(name, newStartTime);
    },
    []
  );

  const onSuccess = useCallback((): void => {
    onCreate && onCreate();
  }, [onCreate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        const { schema } = new CreateIntervalValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        const { intervalType, startDate, endDate, dockIds, ...rest } = validData;

        const dockIdsArray = Array.isArray(dockIds)
          ? dockIds.map((dock: any) => Number(dock.value ?? dock))
          : [];

        const expandDates = (start: Date, end: Date, weekdaysOnly = false) => {
          const dates: string[] = [];
          const current = new Date(start);
          const endDateObj = new Date(end);

          while (current <= endDateObj) {
            const isWeekend = current.getDay() === 0 || current.getDay() === 6;
            if (!weekdaysOnly || !isWeekend) {
              dates.push(current.toISOString().split("T")[0]);
            }
            current.setDate(current.getDate() + 1);
          }
          return dates;
        };

        const createIntervalForDock = (dockId: any, date: string) => {
          dispatch(
            CreateActions.request(
              { ...rest, dockId, startDate: date, endDate: date },
              onSuccess
            )
          );
        };

        if (intervalType === "single") {
          dockIdsArray.forEach((dockId: any) => {
            dispatch(
              CreateActions.request(
                { ...rest, dockId, startDate, endDate: startDate },
                onSuccess
              )
            );
          });
        } else if (intervalType === "period") {
          const dates = expandDates(startDate, endDate, false);
          dockIdsArray.forEach((dockId: any) => {
            dates.forEach((date) => {
              createIntervalForDock(dockId, date);
            });
          });
        } else if (intervalType === "weekdays") {
          const dates = expandDates(startDate, endDate, true);
          dockIdsArray.forEach((dockId: any) => {
            dates.forEach((date) => {
              createIntervalForDock(dockId, date);
            });
          });
        }
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess]
  );

  const lockOptionsAccordingToUserProfile = useCallback((): void => {
    if (!profile) return;

    const { warehouse } = profile;

    if (warehouse?.id) {
      const warehouseOption = warehouseOptions.find(
        (option) => option.value === warehouse.id
      );
      if (warehouseOption) {
        formRef.current?.setFieldValue('warehouseId', warehouseOption);
      }
    }
  }, [profile, warehouseOptions]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    lockOptionsAccordingToUserProfile();
  }, [lockOptionsAccordingToUserProfile]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return (): void => {
      dispatch(CreateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <Select
            name="warehouseId"
            label="Armazém"
            options={warehouseOptions}
            isLoading={loadingWarehouses}
            onChange={onWarehouseChange}
          />
          <Select
            name="dockIds"
            label="Docas"
            options={dockOptions}
            isLoading={loadingDocks}
            isDisabled={!generalState.warehouseId}
            isMulti={true}
          />
        </S.FormRow>

        <S.FormRow>
          <Select
            name="intervalType"
            label="Tipo de intervalo"
            options={[
              { value: 'period', label: 'Por período' },
              { value: 'single', label: 'Dia único' },
              { value: 'weekdays', label: 'Dias úteis' },
            ]}
            onChange={(option) => setIntervalType(option?.value as any)}
            value={
              intervalType
                ? {
                    value: intervalType,
                    label:
                      intervalType === 'period'
                        ? 'Por período'
                        : intervalType === 'single'
                        ? 'Dia único'
                        : 'Dias úteis',
                  }
                : null
            }
          />
          <Input
            name="startDate"
            label={intervalType === 'single' ? 'Data' : 'Data de início'}
            type="date"
            onChange={(e) => {
              const value = e.target.value;
              if (intervalType === 'single') {
                formRef.current?.setFieldValue('endDate', value);
              }
            }}
          />

          <Input
            name="endDate"
            label="Data de término"
            type="date"
            disabled={intervalType !== 'period' && intervalType !== 'weekdays'}
          />

          <Input
            name="startTime"
            label="Hora de início"
            type="time"
            onBlur={onTimeBlur}
          />
          <Input
            name="endTime"
            label="Hora de término"
            type="time"
            onBlur={onTimeBlur}
          />
        </S.FormRow>

        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/bloqueio-de-horarios">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {creatingInterval ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
